import React, { useState, useEffect } from 'react';
import {
	Box,
	Paper,
	Typography,
	Button,
	Divider,
	List,
	ListItem,
	ListItemButton,
	ListItemText,
	useTheme,
	useMediaQuery,
	Chip,
	CircularProgress,
	Alert
} from '@mui/material';
import { 
	Work, 
	LocationOn, 
	AccessTime, 
	Business,
	Description,
	CheckCircle,
	Star
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { fetchJobs, setSelectedJob, selectJobs } from '../../store/slices/jobsSlice';
import JobApplicationModal from './components/JobApplicationModal';
import { Job } from './types';
import { useTheme as useAppTheme } from '../../contexts/ThemeContext';

const CareersPage = () => {
	const [applyModalOpen, setApplyModalOpen] = useState(false);

	const muiTheme = useTheme();
	const { mode } = useAppTheme();
	const isSmDown = useMediaQuery(muiTheme.breakpoints.down('sm'));
	const showSidebar = !isSmDown;
	const showTopList = isSmDown;

	const dispatch = useAppDispatch();
	const { jobs, selectedJob, loading, error } = useAppSelector(selectJobs);

	useEffect(() => {
		dispatch(fetchJobs());
	}, [dispatch]);

	const handleOpenApplyModal = () => {
		setApplyModalOpen(true);
	};

	const handleCloseApplyModal = () => {
		setApplyModalOpen(false);
	};

	const handleJobSelect = (job: Job) => {
		dispatch(setSelectedJob(job));
	};

	// Helper function to split text by newlines and filter out empty strings
	const splitText = (text: string | null | undefined) => {
		if (!text) return [];
		return text
			.split('\n')
			.map(item => item.trim())
			.filter(item => item.length > 0 && item !== '•');
	};

	// Dynamic background color based on theme
	const getBackgroundColor = () => {
		return mode === 'dark' ? '#111112' : '#f8f9fa';
	};

	// Dynamic text color based on theme
	const getTextColor = () => {
		return mode === 'dark' ? 'white' : muiTheme.palette.text.primary;
	};

	if (loading === 'pending') {
		return (
			<Box sx={{ 
				minHeight: '100vh', 
				display: 'flex', 
				justifyContent: 'center', 
				alignItems: 'center',
				bgcolor: getBackgroundColor(),
				color: getTextColor()
			}}>
				<CircularProgress sx={{ color: 'secondary.main' }} />
			</Box>
		);
	}

	if (loading === 'failed') {
		return (
			<Box sx={{ 
				minHeight: '100vh', 
				bgcolor: getBackgroundColor(),
				color: getTextColor(), 
				py: 4, 
				px: 2 
			}}>
				<Alert severity="error" sx={{ mb: 2 }}>
					{error || 'Failed to load jobs'}
				</Alert>
				<Button 
					variant="contained" 
					onClick={() => dispatch(fetchJobs())}
					sx={{ bgcolor: 'secondary.main' }}
				>
					Retry
				</Button>
			</Box>
		);
	}

	return (
		<Box sx={{ 
			minHeight: '100vh', 
			bgcolor: getBackgroundColor(),
			color: getTextColor(), 
			py: 0 
		}}>
			{/* Landing Header */}
			<Box sx={{ 
				width: '100%', 
				display: 'flex', 
				flexDirection: 'column', 
				justifyContent: 'end', 
				alignItems: 'center', 
				minHeight: '200px', 
				height: '30vh' 
			}}>
				<Typography variant="h3" sx={{ fontWeight: 700, mb: 2, color: 'secondary.main', textAlign: 'center' }}>
					Join Our Team
				</Typography>
				<Typography variant="h6" sx={{ 
					color: mode === 'dark' ? 'grey.400' : 'grey.600', 
					textAlign: 'center', 
					maxWidth: 600, 
					px: 2 
				}}>
					Be part of a dynamic team that's revolutionizing the courier services industry
				</Typography>
			</Box>
			
			<Divider sx={{ 
				my: 4, 
				borderColor: mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)' 
			}} />

			<motion.div
				initial={{ opacity: 0, y: 40 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.7, ease: [0.4, 0.1, 0.2, 1] }}
			>
				<Paper
					sx={{
						flex: 1,
						display: 'flex',
						flexDirection: isSmDown ? 'column' : 'row',
						minHeight: 600,
						borderRadius: 4,
						overflow: 'hidden',
						mx: { xs: 1, sm: 2, md: 10 },
						p: isSmDown ? 1 : 0,
					}}
				>
					{/* Top List for small screens */}
					{showTopList && (
						<Box sx={{
							width: '100%',
							display: 'flex',
							overflowX: 'auto',
							pb: 1,
							mb: 2,
							gap: 1,
						}}>
							{jobs.map((job) => (
								<ListItem key={job.id} disablePadding sx={{ width: 'auto', minWidth: 200 }}>
									<ListItemButton
										selected={selectedJob?.id === job.id}
										onClick={() => handleJobSelect(job)}
										sx={{
											borderRadius: 2,
											mx: 1,
											height: '100%',
											my: 0.5,
											minWidth: 180,
											bgcolor: selectedJob?.id === job.id 
												? 'secondary.main' 
												: mode === 'dark' ? '#28292b' : '#f5f5f5',
											color: selectedJob?.id === job.id 
												? 'white' 
												: mode === 'dark' ? 'white' : muiTheme.palette.text.primary,
											'&:hover': {
												bgcolor: 'secondary.main',
												color: 'white',
											},
										}}
									>
										<ListItemText
											primary={job.title}
											secondary={job.job_type}
											primaryTypographyProps={{ 
												fontWeight: 600, 
												color: selectedJob?.id === job.id 
													? 'white' 
													: mode === 'dark' ? 'white' : muiTheme.palette.text.primary, 
												fontSize: 14 
											}}
											secondaryTypographyProps={{ 
												color: selectedJob?.id === job.id 
													? 'grey.300' 
													: mode === 'dark' ? 'grey.400' : 'grey.600', 
												fontSize: 12 
											}}
										/>
									</ListItemButton>
								</ListItem>
							))}
						</Box>
					)}

					{/* Sidebar for desktop/tablet */}
					{showSidebar && (
						<Box sx={{ 
							width: { xs: 120, sm: 280 }, 
							bgcolor: '#28292b', 
							borderRight: '1px solid #333', 
							pt: 2, 
							px: 1 
						}}>
							<Typography variant="h6" sx={{ px: 2, mb: 2, color: 'secondary.main', fontWeight: 600 }}>
								Open Positions
							</Typography>
							<List>
								{jobs.map((job) => (
									<ListItem key={job.id} disablePadding>
										<ListItemButton
											selected={selectedJob?.id === job.id}
											onClick={() => handleJobSelect(job)}
											sx={{
												borderRadius: 2,
												mx: 1,
												my: 0.5,
												'&.Mui-selected': {
													bgcolor: 'secondary.main',
													'&:hover': {
														bgcolor: 'secondary.main',
														color: 'white',
													},
												},
											}}
										>
											<ListItemText
												primary={job.title}
												secondary={
													<Box component="span">
														<Typography variant="body2" color="grey.400" component="span">
															{job.job_type}
														</Typography>
														<br />
														<Typography variant="caption" color="grey.500" component="span">
															{job.location}
														</Typography>
													</Box>
												}
												primaryTypographyProps={{ fontWeight: 600, color: 'white', fontSize: 14 }}
												secondaryTypographyProps={{ fontSize: 12 }}
											/>
										</ListItemButton>
									</ListItem>
								))}
							</List>
						</Box>
					)}

					{/* Job Details section */}
					<Box
						sx={{
							flex: 1,
							p: { xs: 2, sm: 3, md: 4 },
							display: 'flex',
							flexDirection: 'column',
							minWidth: 0,
						}}
					>
						{selectedJob ? (
							<>
								{/* Job Header */}
								<Box sx={{ mb: 3 }}>
									<Typography variant="h4" sx={{ fontWeight: 700, mb: 1, color: 'secondary.main' }}>
										{selectedJob.title}
									</Typography>
									<Typography variant="h6" sx={{ color: mode === 'dark' ? 'grey.400' : 'grey.600', mb: 2 }}>
										{selectedJob.job_type}
									</Typography>
									
									{/* Job Meta Info */}
									<Box sx={{ 
										display: 'grid', 
										gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr 1fr' },
										gap: 2, 
										mb: 3 
									}}>
										<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
											<LocationOn sx={{ color: 'secondary.main', fontSize: 20 }} />
											<Typography variant="body2" color={mode === 'dark' ? 'grey.300' : 'grey.600'}>
												{selectedJob.location}
											</Typography>
										</Box>
										<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
											<AccessTime sx={{ color: 'secondary.main', fontSize: 20 }} />
											<Typography variant="body2" color={mode === 'dark' ? 'grey.300' : 'grey.600'}>
												{selectedJob.job_type}
											</Typography>
										</Box>
										<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
											<Work sx={{ color: 'secondary.main', fontSize: 20 }} />
											<Typography variant="body2" color={mode === 'dark' ? 'grey.300' : 'grey.600'}>
												{selectedJob.required_experience}
											</Typography>
										</Box>
										<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
											<Business sx={{ color: 'secondary.main', fontSize: 20 }} />
											<Typography variant="body2" color={mode === 'dark' ? 'grey.300' : 'grey.600'}>
												{selectedJob.salary}
											</Typography>
										</Box>
									</Box>

									<Chip 
										label={`Posted: ${new Date(selectedJob.created_at).toLocaleDateString()}`}
										sx={{ 
											bgcolor: 'secondary.main', 
											color: 'white',
											fontWeight: 600
										}}
									/>
								</Box>

								<Divider sx={{ mb: 3, borderColor: mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)' }} />

								{/* Job Description */}
								<Box sx={{ mb: 4 }}>
									<Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: mode === 'dark' ? 'white' : muiTheme.palette.text.primary, display: 'flex', alignItems: 'center', gap: 1 }}>
										<Description sx={{ color: 'secondary.main' }} />
										Job Description
									</Typography>
									<Typography variant="body1" sx={{ color: mode === 'dark' ? 'grey.300' : 'grey.700', lineHeight: 1.6 }}>
										{selectedJob.description}
									</Typography>
								</Box>

								{/* Requirements */}
								{splitText(selectedJob.requirements).length > 0 && (
									<Box sx={{ mb: 4 }}>
										<Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: mode === 'dark' ? 'white' : muiTheme.palette.text.primary, display: 'flex', alignItems: 'center', gap: 1 }}>
											<CheckCircle sx={{ color: 'secondary.main' }} />
											Requirements
										</Typography>
										<List sx={{ py: 0 }}>
											{splitText(selectedJob.requirements).map((req, index) => (
												<ListItem key={index} sx={{ py: 0.5, px: 0 }}>
													<Typography variant="body2" sx={{ color: mode === 'dark' ? 'white' : muiTheme.palette.text.primary }}>
														{req}
													</Typography>
												</ListItem>
											))}
										</List>
									</Box>
								)}

								{/* Responsibilities */}
								{splitText(selectedJob.responsibilities).length > 0 && (
									<Box sx={{ mb: 4 }}>
										<Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
											<Work sx={{ color: 'secondary.main' }} />
											Key Responsibilities
										</Typography>
										<List sx={{ py: 0 }}>
											{splitText(selectedJob.responsibilities).map((resp, index) => (
												<ListItem key={index} sx={{ py: 0.5, px: 0 }}>
													<Box sx={{ 
														width: 6, 
														height: 6, 
														borderRadius: '50%', 
														bgcolor: 'secondary.main', 
														mr: 2, 
														mt: 1 
													}} />
													<Typography variant="body2" sx={{ color: 'grey.300' }}>
														{resp}
													</Typography>
												</ListItem>
											))}
										</List>
									</Box>
								)}

								{/* Benefits */}
								{splitText(selectedJob.benefits).length > 0 && (
									<Box sx={{ mb: 4 }}>
										<Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
											<Star sx={{ color: 'secondary.main' }} />
											Benefits & Perks
										</Typography>
										<List sx={{ py: 0 }}>
											{splitText(selectedJob.benefits).map((benefit, index) => (
												<ListItem key={index} sx={{ py: 0.5, px: 0 }}>
													<Box sx={{ 
														width: 6, 
														height: 6, 
														borderRadius: '50%', 
														bgcolor: 'secondary.main', 
														mr: 2, 
														mt: 1 
													}} />
													<Typography variant="body2" sx={{ color: 'grey.300' }}>
														{benefit}
													</Typography>
												</ListItem>
											))}
										</List>
									</Box>
								)}

								{/* Apply Button */}
								<Box sx={{ pt: 3 }}>
									<Button
										variant="contained"
										size="large"
										onClick={handleOpenApplyModal}
										sx={{
											bgcolor: 'secondary.main',
											color: 'white',
											px: 4,
											py: 1.5,
											fontSize: 16,
											fontWeight: 600,
											'&:hover': {
												bgcolor: 'secondary.dark',
											},
										}}
									>
										Apply for this Position
									</Button>
								</Box>
							</>
						) : (
							<Typography color="grey.400" sx={{ textAlign: 'center', fontSize: 20 }}>
								Select a job to view details.
							</Typography>
						)}
					</Box>
				</Paper>
			</motion.div>

			{/* Job Application Modal */}
			<JobApplicationModal
				open={applyModalOpen}
				onClose={handleCloseApplyModal}
				selectedJob={selectedJob}
			/>
		</Box>
	);
};

export default CareersPage; 